// ==========================================================================
// Squarebit Store - Copyright (c) 2026 Squarebit LLC. All rights reserved.
// Proprietary and confidential. Not for redistribution.
//
// Squarebit Eye product pages / widget (store front end). The Eye software
// itself is licensed separately under the Squarebit Eye End User License
// Agreement: https://store.squarebitstudios.com/squarebit-eye/eula
// ==========================================================================

import React from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import { CodeBlock, MenuPath, Note, Tip, VarCard } from '../../touchpose/ui';
import { H2, H3, Body, DocHero, DocFooter, Steps, UL_CLASS } from './shared';

// i18n contract: attribute names (pupilRadius, SBE_IrisWidth, ...), node and
// asset names, host menu paths and every code block stay verbatim in every
// language — they live in `components` slots or as raw CodeBlock children,
// never inside a translated string. VarCard `name` is the literal channel
// name; only the description is translated.
const LINK = 'text-blue-link no-underline font-medium';
const M = (s: string) => <MenuPath>{s}</MenuPath>;

const RiggingDoc: React.FC = () => {
  const { t } = useTranslation();

  return (
    <article className="font-body text-content-primary max-w-[760px]">
      <DocHero
        title={t('eyeDocs.rigging.title')}
        intro={t('eyeDocs.rigging.intro')}
      />

      <H2 id="projector">{t('eyeDocs.layout.projector')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.rigging.projectorIntro"
          components={{ b: <strong /> }}
        />
      </Body>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.projectorB1"
            components={{
              z: <strong>+Z</strong>,
              x: <strong>+X</strong>
            }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.projectorB2"
            components={{ b: <strong /> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.projectorB3"
            components={{ b: <strong />, vc: <strong>Virtual Cornea</strong> }}
          />
        </li>
        <li>{t('eyeDocs.rigging.projectorB4')}</li>
      </ul>
      <Body>
        <Trans
          i18nKey="eyeDocs.rigging.projectorOutro"
          components={{ b: <strong /> }}
        />
      </Body>

      <H2 id="channels">{t('eyeDocs.layout.channels')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.rigging.channelsIntro"
          components={{
            b: <strong />,
            pr: M('pupilRadius'),
            lb: M('lodBlend'),
            iw: M('irisWidth'),
            ih: M('irisHeight'),
            pw: M('pupilWidth'),
            ph: M('pupilHeight'),
            px: M('pupilOffsetX'),
            py: M('pupilOffsetY')
          }}
        />
      </Body>
      <VarCard name="pupilRadius" type="0.05–0.42" example="0.1667">
        {t('eyeDocs.rigging.chPupilRadius')}
      </VarCard>
      <VarCard name="lodBlend" type="0–1" example="0">
        {t('eyeDocs.rigging.chLodBlend')}
      </VarCard>
      <H3>{t('eyeDocs.rigging.h3ShapeSet')}</H3>
      <Body>
        <Trans
          i18nKey="eyeDocs.rigging.shapeSetIntro"
          components={{ i: <em /> }}
        />
      </Body>
      <VarCard name="irisWidth" type="0.25–2.0" example="1.0">
        <Trans
          i18nKey="eyeDocs.rigging.chIrisWidth"
          components={{ ih: M('irisHeight') }}
        />
      </VarCard>
      <VarCard name="irisHeight" type="0.25–2.0" example="1.0">
        {t('eyeDocs.rigging.chIrisHeight')}
      </VarCard>
      <VarCard name="pupilWidth" type="0.25–2.0" example="1.0">
        <Trans
          i18nKey="eyeDocs.rigging.chPupilWidth"
          components={{ pr: M('pupilRadius'), sq: M('pupilSquareness') }}
        />
      </VarCard>
      <VarCard name="pupilHeight" type="0.25–2.0" example="1.0">
        {t('eyeDocs.rigging.chPupilHeight')}
      </VarCard>
      <VarCard name="pupilOffsetX" type="−1.0–1.0" example="0.0">
        {t('eyeDocs.rigging.chPupilOffsetX')}
      </VarCard>
      <VarCard name="pupilOffsetY" type="−1.0–1.0" example="0.0">
        <Trans
          i18nKey="eyeDocs.rigging.chPupilOffsetY"
          components={{ px: M('pupilOffsetX') }}
        />
      </VarCard>
      <H3>{t('eyeDocs.rigging.h3DrivingPerHost')}</H3>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.drivingMaya"
            components={{
              b: <strong>Maya</strong>,
              pr: M('pupilRadius'),
              lb: M('lodBlend')
            }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.drivingUnreal"
            components={{
              b: <strong>Unreal</strong>,
              pr: M('PupilRadius'),
              lb: M('LodBlend'),
              curve: M('SBE_<Parameter>'),
              c1: M('SBE_PupilRadius'),
              c2: M('SBE_IrisWidth'),
              c3: M('SBE_PupilOffsetX'),
              // NB: not `link` — that is an HTML void element, so a paired
              // <link>…</link> in the translated string parses as an empty
              // self-closing tag and the anchor renders with no text.
              usec: (
                <Link
                  to="/squarebit-eye/docs/rigging#unreal-rig"
                  className={LINK}
                />
              )
            }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.drivingWeb"
            components={{
              b: <strong />,
              pr: M('pupilRadius'),
              lb: M('lodBlend'),
              up: M('updateProjector'),
              ue: M('updateEyeParams')
            }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.drivingUsd"
            components={{
              b: <strong>USD</strong>,
              pr: M('PupilRadius'),
              lb: M('LodBlend'),
              attr: M('squarebitEye:*')
            }}
          />
        </li>
      </ul>

      <H2 id="maya-rig">{t('eyeDocs.layout.mayaRig')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.rigging.mayaIntro"
          components={{
            b: <strong />,
            i: <em />,
            fn: M('tool.create_eye_network()'),
            node: M('nodes["projector"]'),
            xf: M('*_projector'),
            loc: M('squarebitEyeProjector')
          }}
        />
      </Body>
      <CodeBlock language="python">
        {`import maya.cmds as cmds
proj = nodes["projector"]                    # "<name>_projector"

# Anatomical eye: ride the eye joint directly
cmds.parent(proj, "L_eye_JNT")

# Film-standard: pin to the socket, aim at an animator-keyed target
cmds.pointConstraint("L_eyeSocket_JNT", proj, maintainOffset=False)
cmds.aimConstraint("lookAt_L_CTL", proj,
                   aimVector=(0, 0, 1),      # +Z looks out of the eye
                   upVector=(0, 1, 0),
                   worldUpType="objectrotation",
                   worldUpObject="head_JNT") # head roll carries the up axis`}
      </CodeBlock>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.mayaB1"
            components={{
              b: <strong>(0, 0, 1)</strong>,
              t1: M('worldUpType'),
              t2: M('objectrotation'),
              t3: M('scene')
            }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.mayaB2"
            components={{ i: <em /> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.mayaB3"
            components={{ pc: M('parentConstraint') }}
          />
        </li>
      </ul>
      <H3>{t('eyeDocs.rigging.h3MayaChannels')}</H3>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.mayaChannels"
            components={{
              b: <strong />,
              pr: M('pupilRadius'),
              lb: M('lodBlend'),
              iw: M('irisWidth'),
              ih: M('irisHeight'),
              pw: M('pupilWidth'),
              ph: M('pupilHeight'),
              px: M('pupilOffsetX'),
              py: M('pupilOffsetY')
            }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.mayaLook"
            components={{
              b: <strong />,
              params: (
                <Link to="/squarebit-eye/docs/parameters" className={LINK} />
              ),
              shader: M('squarebitEyeShader'),
              a1: M('irisInnerColor'),
              a2: M('irisOuterColor'),
              a3: M('irisFiberDetail'),
              a4: M('irisSpotDensity'),
              a5: M('irisHetero*'),
              fn: M('tool.update_procedural_iris(nodes)')
            }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.mayaCaustic"
            components={{
              b: <strong />,
              fn: M('tool.bind_caustic_light(nodes, light_transform)'),
              attr: M('lightDirWorld')
            }}
          />
        </li>
      </ul>

      <H2 id="unreal-rig">{t('eyeDocs.layout.unrealRig')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.rigging.unrealIntro"
          components={{
            comp: <strong>Squarebit Eye</strong>,
            menu: M('Add ▸ Squarebit Eye'),
            i: <em />,
            b: <strong />
          }}
        />
      </Body>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.unrealHook1"
            components={{
              b: <strong />,
              menu: M('Details ▸ Sockets ▸ Parent Socket'),
              x: <strong>+X</strong>
            }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.unrealHook2"
            components={{
              b: <strong />,
              i: <em />,
              curve: M('SBE_<Parameter>')
            }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.unrealHook3"
            components={{
              b: <strong />,
              node: <strong>Update Eye Projector</strong>,
              p1: M('Targets'),
              p2: M('EyeIndex'),
              p3: M('ProjectorWorld'),
              p4: M('PupilRadius'),
              p5: M('LodBlend')
            }}
          />
        </li>
      </ul>
      <Body>{t('eyeDocs.rigging.unrealPushes')}</Body>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.unrealB1"
            components={{ pr: M('PupilRadius'), lb: M('LodBlend') }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.unrealB2"
            components={{ b: <strong>Squarebit Subdivs</strong> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.unrealB3"
            components={{ b: <strong>Virtual Cornea</strong>, i: <em /> }}
          />
        </li>
      </ul>
      <H3>{t('eyeDocs.rigging.h3Sequencer')}</H3>
      <Body>{t('eyeDocs.rigging.sequencerIntro')}</Body>
      <Body>
        <Trans
          i18nKey="eyeDocs.rigging.sequencerCtor"
          components={{ b: <strong />, i: <em /> }}
        />
      </Body>
      <Note label={t('eyeDocs.rigging.noteSequencerLabel')}>
        <Trans
          i18nKey="eyeDocs.rigging.noteSequencer"
          components={{ node: M('Assign Material Preserving Animated') }}
        />
      </Note>

      <H3>{t('eyeDocs.rigging.h3ControlRig')}</H3>
      <Body>
        <Trans
          i18nKey="eyeDocs.rigging.controlRigIntro"
          components={{
            curve: M('SBE_<Parameter>'),
            b: <strong>Set Curve Value</strong>
          }}
        />
      </Body>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.crB1"
            components={{
              c1: M('SBE_PupilRadius'),
              c2: M('SBE_LodBlend')
            }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.crB2"
            components={{
              c1: M('SBE_IrisWidth'),
              c2: M('SBE_IrisHeight'),
              c3: M('SBE_PupilWidth'),
              c4: M('SBE_PupilHeight'),
              c5: M('SBE_PupilOffsetX'),
              c6: M('SBE_PupilOffsetY')
            }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.crB3"
            components={{
              i: <em />,
              grp: <strong>Squarebit Eye</strong>,
              c1: M('SBE_Ior'),
              c2: M('SBE_CausticStrength')
            }}
          />
        </li>
      </ul>

      <H2 id="lid-conform">{t('eyeDocs.layout.lidConform')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.rigging.lidIntro"
          components={{ i: <em />, b: <strong>Deformer Graph</strong> }}
        />
      </Body>
      <H3>{t('eyeDocs.rigging.h3LidSetup')}</H3>
      <Steps>
        <><Trans
            i18nKey="eyeDocs.rigging.lidStep1"
            components={{ b: <strong /> }}
          /></>
        <><Trans
            i18nKey="eyeDocs.rigging.lidStep2"
            components={{
              menu: M('Content Browser ▸ Add ▸ Animation ▸ Deformer Graph'),
              node: <strong>Squarebit Eye Field</strong>
            }}
          /></>
        <><Trans
            i18nKey="eyeDocs.rigging.lidStep3"
            components={{
              node: <strong>Custom Compute Kernel</strong>,
              vtx: <strong>Vertex</strong>,
              kernel: M('SquarebitEyeLidConform'),
              n1: M('NumEyes'),
              n2: M('PackA'),
              n3: M('PackE'),
              n4: M('Position'),
              n5: M('OutPosition'),
              gap: <strong>Gap</strong>,
              blend: <strong>Blend</strong>,
              strength: <strong>Strength</strong>
            }}
          /></>
        <><Trans
            i18nKey="eyeDocs.rigging.lidStep4"
            components={{
              file: M('Shaders/LidConformKernel.hlsl'),
              def: M('#define')
            }}
          /></>
        <><Trans
            i18nKey="eyeDocs.rigging.lidStep5"
            components={{
              md: <strong>Mesh Deformer</strong>,
              add: <strong>Add Deformer</strong>
            }}
          /></>
      </Steps>
      <H3>{t('eyeDocs.rigging.h3LidTuning')}</H3>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.lidTune1"
            components={{
              gap: M('Gap'),
              blend: M('Blend'),
              strength: M('Strength')
            }}
          />
        </li>
        <li>{t('eyeDocs.rigging.lidTune2')}</li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.lidTune3"
            components={{ gap: M('Gap') }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.lidTune4"
            components={{ menu: M('Squarebit Eye ▸ Conform') }}
          />
        </li>
      </ul>
      <Tip label={t('eyeDocs.rigging.tipSubdivsLabel')}>
        <Trans
          i18nKey="eyeDocs.rigging.tipSubdivs"
          components={{ b: <strong>Deformer Graph Support</strong>, i: <em /> }}
        />
      </Tip>

      <H2 id="blender-rig">{t('eyeDocs.layout.blenderRig')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.rigging.blenderIntro"
          components={{
            b: <strong />,
            empty: M('*_projector'),
            pr: M('pupil_radius'),
            lb: M('lod_blend'),
            z: <strong>+Z</strong>
          }}
        />
      </Body>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.blenderB1"
            components={{
              b: <strong />,
              menu: M('Ctrl+P ▸ Bone'),
              childOf: <strong>Child Of</strong>,
              inv: M('Set Inverse')
            }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.blenderB2"
            components={{
              b: <strong />,
              damped: <strong>Damped Track</strong>,
              z: <strong>Z</strong>,
              trackTo: <strong>Track To</strong>,
              z2: <strong>Z</strong>
            }}
          />
        </li>
        <li>{t('eyeDocs.rigging.blenderB3')}</li>
      </ul>

      <H2 id="web-rig">{t('eyeDocs.layout.webRig')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.rigging.webIntro"
          components={{
            fn: M('updateProjector(eye, object3D, opts)'),
            mw: M('matrixWorld'),
            o: M('Object3D'),
            bone: M('Bone')
          }}
        />
      </Body>
      <CodeBlock language="javascript">
        {`const projector = new THREE.Object3D();
head.add(projector);                      // rides the head like a bone would
projector.position.set(0.32, 0.35, 0.9);  // eye center, in head space
projector.scale.setScalar(0.12);          // eye radius in world units
// per frame — lookAt aims +Z, the projector convention:
projector.lookAt(lookTarget.getWorldPosition(new THREE.Vector3()));
updateProjector(eye, projector, { pupilRadius: 0.1667, lodBlend: 0 });`}
      </CodeBlock>
      <Body>
        <Trans
          i18nKey="eyeDocs.rigging.webForwardAxis"
          components={{ opt: M("{ forwardAxis: 'negz' }") }}
        />
      </Body>
      <Body>{t('eyeDocs.rigging.webPerFrame')}</Body>
      <CodeBlock language="javascript">
        {`projector.position.copy(eyeCenter);
projector.lookAt(lookTarget.position);
updateProjector(eye, projector, { pupilRadius: 0.1667, lodBlend: 0 });
updateEyeParams(eye, {           // the shape set, keyable per frame too
  irisWidth: 1.0, irisHeight: squint,      // narrow with the lids
  pupilWidth: 1.0, pupilHeight: 1.0,
  pupilOffsetX: focusDrift, pupilOffsetY: 0.0,
});
updateLightDir(eye, keyLight); // caustic / bleed / limbus light response`}
      </CodeBlock>
      <Note label={t('eyeDocs.rigging.noteSanityLabel')}>
        <Trans
          i18nKey="eyeDocs.rigging.noteSanity"
          components={{ fn: M('updateProjector') }}
        />
      </Note>

      <H2 id="usd-rig">{t('eyeDocs.layout.usdRig')}</H2>
      <Body>
        <Trans
          i18nKey="eyeDocs.rigging.usdIntro"
          components={{
            b: <strong />,
            asset: M('SquarebitEye.usda'),
            prim: M('/SquarebitEye'),
            mesh: M('/SquarebitEye/Eyeball')
          }}
        />
      </Body>
      <CodeBlock language="text">
        {`def Xform "L_eye" (kind = "group")
{
    # your rig writes this transform — the eye follows
    double3 xformOp:translate = (3.1, 4.2, 8.9)
    float3 xformOp:rotateXYZ = (0, -8, 0)
    float3 xformOp:scale = (1.2, 1.2, 1.2)   # eye radius (scene is in cm)
    uniform token[] xformOpOrder = ["xformOp:translate",
                                    "xformOp:rotateXYZ", "xformOp:scale"]

    def "Eye" (prepend references = @./SquarebitEye.usda@) {}
}`}
      </CodeBlock>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.usdB1"
            components={{
              host: <strong>Houdini / Solaris</strong>,
              ref: <strong>Reference LOP</strong>,
              asset: M('SquarebitEye.usda'),
              xf: <strong>Transform LOP</strong>,
              ed: <strong>Edit LOP</strong>,
              prim: M('/SquarebitEye')
            }}
          />
        </li>
        <li>{t('eyeDocs.rigging.usdB2')}</li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.usdB3"
            values={{ section: t('eyeDocs.layout.channels') }}
            components={{
              i: <em />,
              ch: (
                <Link
                  to="/squarebit-eye/docs/rigging#channels"
                  className={LINK}
                />
              )
            }}
          />
        </li>
      </ul>
      <H3>{t('eyeDocs.rigging.h3HoudiniRealtime')}</H3>
      <Body>
        <Trans
          i18nKey="eyeDocs.rigging.houdiniGlIntro"
          components={{
            hda: M('sbe_eye_glsl.hda'),
            p1: M('sbeProjOrigin'),
            p2: M('sbeProjFwd'),
            p3: M('sbeProjUp'),
            p4: M('sbeProjScale'),
            p5: M('sbePupilRadius')
          }}
        />
      </Body>
      <CodeBlock language="python">
        {`import hou
null = hou.node("/obj").createNode("null", "eye_projector")
mat  = hou.node("/mat/sbe_eye")
rows = {"sbeProjOrigin": 3, "sbeProjFwd": 2, "sbeProjUp": 1}   # xform rows
for base, row in rows.items():
    for i, comp in enumerate("xyz"):
        mat.parm(base + comp).setExpression(
            "hou.node('/obj/eye_projector').worldTransform()"
            ".asTupleOfTuples()[%d][%d]" % (row, i),
            language=hou.exprLanguage.Python)`}
      </CodeBlock>
      <ul className={UL_CLASS}>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.houdiniB1"
            components={{ b: <strong /> }}
          />
        </li>
        <li>
          <Trans
            i18nKey="eyeDocs.rigging.houdiniB2"
            components={{
              i: <em />,
              expr: M("hou.node('/obj/eye').worldTransform().inverted()"),
              scale: M('sbeProjScale')
            }}
          />
        </li>
        <li>{t('eyeDocs.rigging.houdiniB3')}</li>
      </ul>

      <DocFooter current="/squarebit-eye/docs/rigging" />
    </article>
  );
};

export default RiggingDoc;
